import type { FileManagerPermission } from '@/composeables/file-manager-capabilities';
import { inject, provide, ref, type InjectionKey, type Ref } from 'vue';
import { computePreviewMode } from '../../permissions/modes';
import type { FileSystemItem } from '../../types';

export type FileManagerPreviewState = {
  previewItem: Ref<FileSystemItem | null>;
  isPreviewOpen: Ref<boolean>;
  openPreview: (item: FileSystemItem) => void;
  closePreview: () => void;
};

export const fileManagerPreviewKey: InjectionKey<FileManagerPreviewState> =
  Symbol('fileManagerPreview');

/**
 * Pure: may this item be previewed at all. Both halves matter and neither implies the other — the
 * capability is per-mount, so it is asked about the item's own path, and Graph's preview action is
 * addressed by driveId/id, which a row only carries once its listing resolved them. Exported so
 * every entry point into the preview (the details panel's thumbnail, a grid row's double-click, and
 * whatever opens it next) shares one gate instead of restating it, and so it can be unit-tested
 * directly. Mirrors useFilePreviewQuery's own `enabled` guard.
 */
export const canPreviewItem = (
  item: FileSystemItem | null,
  permissions: FileManagerPermission[],
): boolean =>
  !!item &&
  item.type === 'file' &&
  computePreviewMode(permissions) === 'enabled' &&
  !!item.driveId &&
  !!item.id;

// The previewed item is tracked here rather than read off the details panel. The two used to be the
// same thing, back when the thumbnail was the only way in; now a grid row can open a preview too, and
// tying the modal to `detailsItem` would make it depend on the panel having been pointed at the right
// item first — an implicit ordering between two unrelated gestures. Callers name their item instead.
export const provideFileManagerPreview = () => {
  const previewItem = ref<FileSystemItem | null>(null);
  const isPreviewOpen = ref(false);

  const openPreview = (item: FileSystemItem) => {
    previewItem.value = item;
    isPreviewOpen.value = true;
  };

  const closePreview = () => {
    isPreviewOpen.value = false;
    previewItem.value = null;
  };

  const state: FileManagerPreviewState = {
    previewItem,
    isPreviewOpen,
    openPreview,
    closePreview,
  };
  provide(fileManagerPreviewKey, state);
  return state;
};

export const useFileManagerPreview = () => {
  const state = inject(fileManagerPreviewKey);
  if (!state) {
    throw new Error(
      'useFileManagerPreview() must be called within a component tree provided by provideFileManagerPreview()',
    );
  }
  return state;
};
